import React, { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  resetCrossClientResults,
  selectCrossClientPageResults,
  setCrossClientProgressStatuses,
  setCrossClientResultsExecution,
  setCrossClientResultsExecutionLoading,
  setCrossClientSelectedTab,
  setCrossClientTeammates,
  setCrossClientTeammatesLoading,
} from '../../../store/crossClient/crossClientResults'
import { useLazyGetCrossClientQueryExecutionQuery } from '../../../store/crossClient/crossClient'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useLazyGetAllUsersQuery } from '../../../store/user/user'
import { CROSS_CLIENT_RESULT_TAB_ORDER, type CrossClientResultTab } from '../../../models/CrossClient'
import { useFetchMissingCrossClientClients } from './hooks'

export interface CrossClientResultsInitializationProps {
  executionId: string
}

export const CrossClientResultsInitialization: React.FC<CrossClientResultsInitializationProps> = ({ executionId }) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const pageResults = useAppSelector(selectCrossClientPageResults)

  const [searchParams] = useSearchParams()
  const initialTabRef = useRef(searchParams.get('tab'))

  const [triggerGetCrossClientQueryExecution] = useLazyGetCrossClientQueryExecutionQuery()
  const [triggerGetAllUsers] = useLazyGetAllUsersQuery()
  const fetchMissingClients = useFetchMissingCrossClientClients()

  useEffect(() => {
    const tab = initialTabRef.current
    if (tab && (CROSS_CLIENT_RESULT_TAB_ORDER as string[]).includes(tab)) {
      dispatch(setCrossClientSelectedTab(tab as CrossClientResultTab))
    }
  }, [dispatch])

  useEffect(() => {
    const getExecution = async () => {
      try {
        dispatch(setCrossClientResultsExecutionLoading(true))
        const execution = await triggerGetCrossClientQueryExecution({ accountId, executionId, expand: ['matches', 'progress', 'evaluationCounts'] }).unwrap()
        if (!execution) {
          return
        }
        dispatch(setCrossClientResultsExecution(execution))
        dispatch(setCrossClientProgressStatuses(execution.progress?.statuses ?? {}))
      } catch (e) {
        console.error(e)
      } finally {
        dispatch(setCrossClientResultsExecutionLoading(false))
      }
    }
    getExecution()
  }, [dispatch, accountId, executionId, triggerGetCrossClientQueryExecution])

  useEffect(() => {
    const getCrossClientTeammates = async () => {
      try {
        const teammates = await triggerGetAllUsers({ accountId, identityTypes: ['idp'] }).unwrap()
        if (teammates?.data) {
          dispatch(setCrossClientTeammates(teammates.data))
        }
      } finally {
        dispatch(setCrossClientTeammatesLoading(false))
      }
    }
    getCrossClientTeammates()
  }, [dispatch, accountId, triggerGetAllUsers])

  useEffect(() => {
    if (!pageResults.length) {
      return
    }
    fetchMissingClients(pageResults.map((result) => result.clientId)).catch(() => {})
  }, [pageResults, fetchMissingClients])

  useEffect(() => {
    return () => {
      dispatch(resetCrossClientResults())
    }
  }, [dispatch])

  return null
}
